import { auth } from "@lumberset/api-client";
import { Button, Field, Heading, Hint, Input } from "@lumberset/ui";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { isAdmin, NOT_ADMIN_MESSAGE } from "../../lib/admin";
import { isUnconfirmedEmail, UnconfirmedEmail } from "./-unconfirmed-email";

interface LoginSearch {
  redirect?: string;
  /** Set by the set-password page once a new password is saved. */
  notice?: "password-set";
}

export const Route = createFileRoute("/_auth/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    notice: search.notice === "password-set" ? "password-set" : undefined,
  }),
  component: Login,
});

function Login() {
  const router = useRouter();
  const search = Route.useSearch();
  const [error, setError] = useState<string | null>(null);
  /** The address to confirm, once a right password met an unconfirmed email. */
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email"));
    setBusy(true);
    setError(null);
    setUnconfirmed(null);

    const result = await auth.signIn.email({
      email,
      password: String(form.get("password")),
    });

    if (result.error) {
      setBusy(false);
      if (isUnconfirmedEmail(result.error)) setUnconfirmed(email);
      else setError(result.error.message ?? "That email and password do not match.");
      return;
    }

    // Only an Admin comes in. Anyone else is signed straight back out.
    let admin: boolean;
    try {
      admin = await isAdmin();
    } catch (failure) {
      setBusy(false);
      setError(failure instanceof Error ? failure.message : "Something went wrong.");
      return;
    }
    if (!admin) {
      await auth.signOut();
      setBusy(false);
      setError(NOT_ADMIN_MESSAGE);
      return;
    }

    // Only follow a redirect back into this panel, never to another site.
    const target = search.redirect?.startsWith("/") ? search.redirect : "/";
    await router.invalidate();
    router.history.push(target);
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-y-5">
      <div>
        <Heading>Sign in</Heading>
        <p className="txt-small mt-1 text-ui-fg-subtle">
          Use your LumberSet account. Only Admins can open this panel.
        </p>
      </div>

      {search.notice === "password-set" && !error && !unconfirmed ? (
        <p className="txt-small rounded-lg bg-ui-bg-subtle px-3 py-2 text-ui-fg-base" role="status">
          Your password is saved. Sign in with it now.
        </p>
      ) : null}

      <Field label="Email">
        {(control) => (
          <Input
            {...control}
            name="email"
            type="email"
            autoComplete="email"
            required
            size="large"
            autoFocus
          />
        )}
      </Field>

      <Field label="Password">
        {(control) => (
          <Input
            {...control}
            name="password"
            type="password"
            autoComplete="current-password"
            required
            size="large"
          />
        )}
      </Field>

      {unconfirmed ? <UnconfirmedEmail email={unconfirmed} /> : null}
      {error ? <Hint variant="error">{error}</Hint> : null}

      <Button type="submit" size="large" block isLoading={busy}>
        Sign in
      </Button>

      <Link
        to="/forgot-password"
        className="txt-small text-center text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
      >
        Forgot your password?
      </Link>
    </form>
  );
}
